import type { ScraperSettings } from '@/lib/scraper'
import { scraperApi } from '@/lib/scraper'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  BTN_PRIMARY,
  CARD,
  INPUT,
  LABEL,
  MUTED,
  SKELETON,
  TEXTAREA,
  cls
} from './ui'

export function SettingsPanel() {
  const queryClient = useQueryClient()
  const settings = useQuery({
    queryKey: ['scraper-settings'],
    queryFn: scraperApi.settings
  })
  const [draft, setDraft] = useState<ScraperSettings | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [required, setRequired] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (settings.data) {
      setDraft(settings.data)
      setRequired(
        JSON.stringify(settings.data.required_fields_per_recipe ?? {}, null, 2)
      )
    }
  }, [settings.data])

  if (settings.isError)
    return <p className="text-sm text-red-500">Could not load settings.</p>
  if (settings.isLoading || !draft)
    return <div className={cls(SKELETON, 'h-96')} />

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    let requiredJson: Record<string, unknown>
    try {
      requiredJson = required.trim()
        ? (JSON.parse(required) as Record<string, unknown>)
        : {}
    } catch {
      toast.error('Required fields must be valid JSON')
      return
    }
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        captcha_provider: draft.captcha_provider,
        free_proxy_pool_enabled: draft.free_proxy_pool_enabled,
        vision_auto_fallback: draft.vision_auto_fallback,
        daily_captcha_budget: draft.daily_captcha_budget,
        daily_vision_page_limit: draft.daily_vision_page_limit,
        required_fields_per_recipe: requiredJson
      }
      if (apiKey) body.captcha_api_key = apiKey
      await scraperApi.updateSettings(body)
      setApiKey('')
      toast.success('Settings saved')
      await queryClient.invalidateQueries({ queryKey: ['scraper-settings'] })
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not save settings'
      )
    } finally {
      setSaving(false)
    }
  }

  const set = <K extends keyof ScraperSettings>(
    key: K,
    value: ScraperSettings[K]
  ) => setDraft({ ...draft, [key]: value })

  return (
    <form onSubmit={save} className="grid gap-4 lg:grid-cols-2">
      <section className={cls(CARD, 'space-y-4')}>
        <h3 className="text-sm font-bold text-neutral-900 dark:text-white">
          Captcha solving
        </h3>
        <p className={MUTED}>
          Cloudflare, image captchas and reCAPTCHA v2 (audio) are tried for free
          first. The paid provider only runs when the free layers fail, within
          the daily budget.
        </p>
        <div>
          <label htmlFor="captcha-provider" className={LABEL}>
            Paid provider
          </label>
          <select
            id="captcha-provider"
            value={draft.captcha_provider}
            onChange={(e) =>
              set(
                'captcha_provider',
                e.target.value as ScraperSettings['captcha_provider']
              )
            }
            className={INPUT}
          >
            <option value="none">None</option>
            <option value="capsolver">CapSolver</option>
            <option value="twocaptcha">2Captcha</option>
          </select>
        </div>
        <div>
          <label htmlFor="captcha-key" className={LABEL}>
            API key (
            {draft.captcha_api_key_status === 'set'
              ? 'saved, leave empty to keep it'
              : 'not set'}
            )
          </label>
          <input
            id="captcha-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="••••••••"
            className={INPUT}
          />
        </div>
        <div>
          <label htmlFor="captcha-budget" className={LABEL}>
            Paid solves per day (used today: {draft.usage_today.captcha})
          </label>
          <input
            id="captcha-budget"
            type="number"
            min={0}
            value={draft.daily_captcha_budget}
            onChange={(e) =>
              set('daily_captcha_budget', Number(e.target.value))
            }
            className={INPUT}
          />
        </div>
      </section>
      <section className={cls(CARD, 'space-y-4')}>
        <h3 className="text-sm font-bold text-neutral-900 dark:text-white">
          Vision and proxies
        </h3>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={draft.vision_auto_fallback}
            onChange={(e) => set('vision_auto_fallback', e.target.checked)}
            className="mt-0.5 size-4 accent-neutral-900 dark:accent-white"
          />
          <span>
            <span className="block text-sm font-semibold text-neutral-900 dark:text-white">
              Read unreadable pages with vision automatically
            </span>
            <span className={MUTED}>
              When every tier returns thin or scrambled text, screenshot tiles
              are read by the vision model.
            </span>
          </span>
        </label>
        <div>
          <label htmlFor="vision-limit" className={LABEL}>
            Vision pages per day (used today: {draft.usage_today.vision})
          </label>
          <input
            id="vision-limit"
            type="number"
            min={0}
            value={draft.daily_vision_page_limit}
            onChange={(e) =>
              set('daily_vision_page_limit', Number(e.target.value))
            }
            className={INPUT}
          />
        </div>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={draft.free_proxy_pool_enabled}
            onChange={(e) => set('free_proxy_pool_enabled', e.target.checked)}
            className="mt-0.5 size-4 accent-neutral-900 dark:accent-white"
          />
          <span>
            <span className="block text-sm font-semibold text-neutral-900 dark:text-white">
              Free proxy pool
            </span>
            <span className={MUTED}>
              Refreshed hourly from public lists and health checked. Slow and
              often blocked, so only a last resort.
            </span>
          </span>
        </label>
      </section>
      <section className={cls(CARD, 'space-y-3 lg:col-span-2')}>
        <h3 className="text-sm font-bold text-neutral-900 dark:text-white">
          Required fields per recipe (overrides)
        </h3>
        <p className={MUTED}>
          Leave empty for the defaults: business needs name, category and one of
          phone, email or address; menu needs 3 priced items; products 1; events
          a title and start time.
        </p>
        <label htmlFor="required-fields" className="sr-only">
          Required fields JSON
        </label>
        <textarea
          id="required-fields"
          value={required}
          onChange={(e) => setRequired(e.target.value)}
          className={TEXTAREA}
          placeholder='{"menu": {"min_items": 5}}'
        />
      </section>
      <div className="lg:col-span-2">
        <button type="submit" disabled={saving} className={BTN_PRIMARY}>
          {saving && <Loader2 className="size-4 animate-spin" />} Save settings
        </button>
      </div>
    </form>
  )
}
